import { Injectable } from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import {
  GetPossibleColumnsResponse,
  IChildNotification,
  IQueryDataForDesignerResponse,
  IQueryKeywordMapping,
  IQueryParameters,
  IQueryTable,
  IQueryTableWithColumnDataList,
  IQueryTableWithQuerySelects,
  IUpdateQueryTableDataUI,
} from "src/app/view-model/query-builder.view-model";
import { cloneDeep, isEqual } from "lodash";
import { MatDialogRef } from "@angular/material/dialog";
import { QuerySliderComponent } from "../components/query-manager/query-slider/query-slider.component";
import { IRuleWithQueryFilters } from "src/app/view-model/query-builder.view-model";
import { DatasetSorting } from "../components/query-manager/query-data-set-editor/data-set-sorting.component";
import { IDropDownOptions } from "@caseworthy/cw-ui-components";
import { GroupByPipe } from "src/app/shared/pipes/group-by.pipe";

@Injectable({
  providedIn: "root",
})
export class DataQueryBuilderService {
  private tempId = -10;
  private queryDataFromDb: IQueryDataForDesignerResponse;
  private queryDataUI: IQueryDataForDesignerResponse;
  private queryChanged = false;
  private querySliderDialog!: MatDialogRef<QuerySliderComponent>;
  private datasetQuerySorting = new DatasetSorting();
  private parentQueryColumnsAsDropDown !: IDropDownOptions[];

  constructor(private groupByPipe: GroupByPipe) {
    this.queryDataFromDb = {} as IQueryDataForDesignerResponse;
    this.queryDataUI = {} as IQueryDataForDesignerResponse;
    this.parentQueryColumnsAsDropDown =[];
  }

  set parentQueryColumns(parentQueryColumns: IDropDownOptions[]) {
    this.parentQueryColumnsAsDropDown = parentQueryColumns;
  }

  get parentQueryColumns(): IDropDownOptions[] {
    return this.parentQueryColumnsAsDropDown;
  }

  setQuerySliderDialog(data: MatDialogRef<QuerySliderComponent>) {
    this.querySliderDialog = data;
  }
  getQuerySliderDialog() {
    return this.querySliderDialog;
  }

  isqueryChanged() {
    // ToDO: need to remove this.queryDataUI.query.queryTables =[]; soon
    if (this.queryDataUI?.query) {
      this.queryDataUI.query.queryTables = [];
    }
    return (
      this.queryChanged && !isEqual(this.queryDataFromDb, this.queryDataUI)
    );
  }

  getNewTempId() {
    return --this.tempId;
  }

  initQueryData(queryDataResonse: any) {
    const resp = queryDataResonse as IQueryDataForDesignerResponse;
    resp.queryTableWithQuerySelects.map(col=>{
      if(col.properties === null || col.properties === undefined){
        col.properties = "IsVisible=true"
      }
    });
    if (!resp?.query?.queryKeywordDTOs) resp.query.queryKeywordDTOs = [];
    this.queryDataFromDb = cloneDeep(resp);
    this.queryDataUI = resp;
    this.emitQuerychanged(false);
  }

  getCurrentQueryId() {
    return this.queryDataUI?.query?.queryID;
  }

  emitQuerychanged(isDataDifferent :boolean =true)
  {
    this.queryChanged = isDataDifferent;
    this.queryUpdatedSubject.next(this.queryDataUI);
  }

  emitSummarizationUpdate(oldValue: number, value: { dataSet: IQueryTableWithQuerySelects, queryCategory: number, close: boolean }, inputValue: boolean) {
    this.summarizationUpdated.next([oldValue, value, inputValue]);
  }

  emitQueryRuleschanged() {
    this.queryRulesUpdatedSubject.next(this.queryDataUI.ruleWithQueryFilters ?? []);
    this.emitQuerychanged();
  }
  /**
   * This method will check if the given columnAlias is already present in the query select and returns 
   * the next available non-duplicate name. This is achieved by adding a number at the end.
   * If the name is non-duplicate, then the same name is returned as it is.
   * @param columnAlias String - ColumnAlias that needs to be checked if already present and is duplicate
   * @returns String - returns the updated columnALias with a number attached to end if it's duplicate, or returns the ColumnAlias as is, if it's not duplicate.
   */
  getDistinctColumnAlias(columnAlias: string): string {
    while (this.isDuplicateColumnAlias(columnAlias) === true) {
      columnAlias = this.getNonDuplicateName(columnAlias);
    }
    return columnAlias;
  }

  private isDuplicateColumnAlias(columnAlias: string): boolean {
    let isDuplicate = false;
    if (this.queryDataUI?.queryTableWithQuerySelects) {
      this.queryDataUI.queryTableWithQuerySelects.forEach((querySelect) => {
        if (querySelect.columnAlias === columnAlias) {
          isDuplicate = true;
        }
      });
    }
    return isDuplicate;
  }

  private getNonDuplicateName(input: string): string {
    //Regex to get the last characters in a string which are numeric.
    const regExp = /(\d+)$/;
    const match = input.match(regExp);
    if (match) {
      const digits = match[0];
      const incrementedNumber = (parseInt(digits) + 1).toString();
      return input.replace(regExp, incrementedNumber.padStart(digits.length, '0'));
    } else {
      return input + '1';
    }
  }

  getCurrentVersionOfQuery() {
    return this.queryDataUI;
  }

  getOriginalVersionOfQuery() {
    return this.queryDataFromDb;
  }

  addTable(tableData: IQueryTable, columndata: IQueryTableWithColumnDataList[]) {
    tableData = tableData as IQueryTable;
    this.queryDataUI.queryTables?.push(tableData);
    // Add copy of columns to list only if table is not alredy included
    if (!this.tableDataExists(tableData.schemaName, tableData.tableName)) {
      this.queryDataUI.queryTableWithColumnDataList?.push(...cloneDeep(columndata));
    }
    this.emitQuerychanged();
  }

  tableDataExists(schema: string, tableName: string): boolean {
    return this.queryDataUI.queryTableWithColumnDataList?.some(x => x.schemaName === schema && x.tableName === tableName);
  }

  // Add, edit or delete rule action when clicked on Done button
  updateTableRules(rules: IRuleWithQueryFilters[]){
    this.queryDataUI.ruleWithQueryFilters = rules;
    // need to emit this to update lines between entities incase if theres any new rules added , removed,
    this.emitQueryRuleschanged();
  }

  updateQueryProperties(queryData:{queryName: string,queryDescription:string,keywords: IQueryKeywordMapping[], sortColumns?: string | null})
  {
    if (this.queryDataUI?.query) {
      this.queryDataUI.query.queryName = queryData.queryName;
      this.queryDataUI.query.description = queryData.queryDescription;
      this.queryDataUI.query.queryKeywordMappings = queryData.keywords;
      if(queryData.sortColumns !== undefined){
        this.queryDataUI.query.sortColumns = queryData.sortColumns;
      }
      this.emitQuerychanged();
    }
  }

  updateTableProperties(tableData: IUpdateQueryTableDataUI) {
    const i =
      this.queryDataUI.queryTables?.findIndex(
        (x: { queryTableId: number }) =>
          x.queryTableId === tableData.queryTableId
      ) ?? -1;
    let tableToUpdate = this.queryDataUI.queryTables.find(table => table.queryTableId == tableData.queryTableId);
    if (tableToUpdate) {
      tableToUpdate.securityEnforced = tableData?.secured || false;
      tableToUpdate.innerJoined = tableData?.required;

      // alias change
      const oldAlias = tableToUpdate.tableAlias;
      const newAlias = tableData?.tableAlias ?? '';
      if (oldAlias != newAlias) {
        tableToUpdate.tableAlias = newAlias;
        // alias updated
        this.updateRules(newAlias, oldAlias);
        // need to update querySelects also
        this.updateQuerySelectsWithNewAlias(tableToUpdate.queryTableId, newAlias)
        // need to update query Sort order also
        this.updateQuerySortColumns(tableToUpdate.queryTableId, oldAlias, newAlias)
      }
    }
    this.emitQuerychanged();
  }

  updateRules(newAlias: string, oldAlias: string) {
    this.queryDataUI?.ruleWithQueryFilters?.forEach(rule => {
      if (rule.leftOperandType == 1 && rule.leftOperand.indexOf(oldAlias + ".") == 0) {
        rule.leftOperand = newAlias + "." + rule.leftOperand.split(".")[1];
      }
      if (rule.rightOperandType == 1 && rule.rightOperand.indexOf(oldAlias + ".") == 0) {
        rule.rightOperand = newAlias + "." + rule.rightOperand.split(".")[1];
      }
    });
  }

  updateQuerySortColumns(queryTableId: number, oldAlias: string, newAlias: string) {
    if (!this.queryDataUI?.query) return;

    let currentSortOrder = this.queryDataUI.query.sortColumns;
    // if sortcolumns exist
    if (currentSortOrder && currentSortOrder != null) {
      let sortColumns = this.datasetQuerySorting.getSortColumnsFromSortOrder(currentSortOrder);
      let selectsToUpdate = this.getQuerySelectsByTableID(queryTableId);

      if (selectsToUpdate.length > 0) {
        let primarysortColumn = sortColumns[0] ?? null;
        let secondarysortColumn = sortColumns[1] ?? null;
        selectsToUpdate.forEach(select => {

          let currentSort = this.datasetQuerySorting.getSortOrderFromCurrentSortColumn(oldAlias, this.datasetQuerySorting.getColumnNameToSort(select), primarysortColumn, secondarysortColumn);
          if (currentSortOrder && currentSort && currentSort != `~NOSORT~`) {
            // this select is part of querySort , need to change tableAlias
            currentSortOrder = this.datasetQuerySorting.replaceAliasInSortOrder(currentSortOrder, oldAlias, newAlias, 0);
          }
        });
        if (currentSortOrder && currentSortOrder !== '') {
          this.queryDataUI.query.sortColumns = currentSortOrder;
        }
      }
    }
  }

  updateQuerySelectsWithNewAlias(queryTableId: number, newAlias: string) {
    let selectsToUpdate = this.getQuerySelectsByTableID(queryTableId);
    selectsToUpdate.forEach(aSelect => aSelect.tableAlias = newAlias)
  }

  getQuerySelectsByTableID(tableId: number) {
    return this.queryDataUI?.queryTableWithQuerySelects?.filter(aSelect => aSelect.queryTableId == tableId) ?? []
  }

  /// Alias name check will be called in 2 scenarios, 
  // 1. while adding new table into query, in this case table id is not required , 
  // 2. while renaming table , we need to make sure we pass queryTableID, so that it will not throw error if old name == new name. 
  // to check if new aliasname alredy exists in queryTables
  // input - tableAlias : new alias name 
  // input -tableId : queryTable id incase of using it whikle renaming, 0 otherwise.
  tableAliasExistsInQuery(tableAlias: string, tableId: number = 0) {
    let tables = this.queryDataUI?.queryTables?.filter(table => table.queryTableId != tableId) ?? [];
    return tables?.some((table: IQueryTable) => table?.tableAlias?.toLowerCase() === tableAlias?.toLowerCase());
  }

  addQueryParamInSaveObject(paramData: IQueryParameters) {
    if (this.queryDataUI?.queryParameters) {
      this.queryDataUI.queryParameters.push(paramData);
      this.emitQuerychanged();
    }
  }

  updateQueryParamInSaveObject(index: number, paramData: IQueryParameters) {
    if (this.queryDataUI?.queryParameters) {
      this.queryDataUI.queryParameters[index] = paramData;
      this.emitQuerychanged();
    }
  }

  deleteQueryParamInSaveObject(data: IQueryDataForDesignerResponse) {
    if (this.queryDataUI) {
      this.queryDataUI.queryParameters = data.queryParameters;
      this.queryDataUI.ruleWithQueryFilters = data.ruleWithQueryFilters;
      this.emitQuerychanged()
    }
  }
  
  addRuleInSaveObject(ruleData: IRuleWithQueryFilters) {
    this.queryDataUI.ruleWithQueryFilters?.push(ruleData);
    this.emitQuerychanged();
  }

  getPropertiesAsDict(properties: string) {
    let map = new Map<string, string>();
    properties?.split('&')?.forEach(kvp => {
      if (kvp != '') {
        let aPair = kvp.split('=');
        // while parsing from string we have to create prop even if its value is empty 
        map.set(aPair[0], aPair[1] ?? '');
      }
    });
    return map;
  }

  removeQueryTable(queryTableId: number) {
    if (!this.queryDataUI) return;

    let tableIndex = this.queryDataUI.queryTables?.findIndex((x) => x.queryTableId === queryTableId) ?? -1;
    let queryTableToRemove = this.queryDataUI.queryTables?.find((x) => x.queryTableId === queryTableId);
    if (tableIndex != -1) {
      // remove querySelects
      let selectsToremove = this.queryDataUI.queryTableWithQuerySelects?.filter(select => select.queryTableId === queryTableId) ?? [];
      if (selectsToremove != undefined  && selectsToremove.length > 0) {
        // modifies original
        selectsToremove.forEach((item) => {
          let index = this.queryDataUI.queryTableWithQuerySelects?.indexOf(item) ?? -1;
          if (index !== -1 && this.queryDataUI.queryTableWithQuerySelects) {
            this.queryDataUI.queryTableWithQuerySelects.splice(index, 1);
            // update sortColumns for Query
            let updatedSort = this.datasetQuerySorting.applySortToDatasetColumn( '~NOSORT~', item.tableAlias, this.datasetQuerySorting.getColumnNameToSort(item), this.getCurrentVersionOfQuery().query?.sortColumns)
            if( updatedSort !=='' && this.queryDataUI?.query)
            {
              this.queryDataUI.query.sortColumns = updatedSort;
            }
          }
        });
        if (this.queryDataUI.queryTableWithQuerySelects) {
          this.updateSortOrderByIndex(this.queryDataUI.queryTableWithQuerySelects)
        }
      }

      // remove QueryFilteres and QueryRules are same here? // direcly linked to queryTableID
      let filtersToremove = this.queryDataUI.ruleWithQueryFilters?.filter(aRule => aRule.queryTableId === queryTableId) ?? [];
      this.removeQueryFilters(filtersToremove);
      // some rules myt be using current tbles coljumns in rules 
      let columnJoinRules = this.queryDataUI.ruleWithQueryFilters?.filter(aRule => aRule.leftOperandType == 1 && aRule.rightOperandType == 1) ?? [];
      let joinsToRemove = columnJoinRules.filter(joinRule => joinRule.leftOperand.indexOf(`${queryTableToRemove?.tableAlias}.`) == 0 || joinRule.rightOperand.indexOf(`${queryTableToRemove?.tableAlias}.`) == 0);
      this.removeQueryFilters(joinsToRemove);
      // This is new bug to be created soon , To remove dependant rules from all tables :
      // how about a rule table1.colum = tabletoremove.column and is linked to table1, need to remove thos as well : thi is not even handled in legacy
      // let rulesWithRemovedTableColumns = this.queryDataUI.ruleWithQueryFilters?.filter(aRule => this.ruleHasKeyword(aRule, queryTableToRemove?.tableAlias, 0)) ?? [];
      // remove rulesWithRemovedTableColumns rules and adjust theeir parent rules ( using removeRule whileremoving queryparams  s code to adjust hybrid rule if one part of rule is removed)
      this.queryDataUI.queryTables?.splice(tableIndex, 1);
      // uopdate sortorder of remainig tables
      this.updateSortOrderByIndex(this.queryDataUI.queryTables)
      this.queryTablesRemovedSubject.next(queryTableId);
      this.emitQuerychanged();
    }
  }

  /**
   * 
   * @param rule -- Rule to validate
   * @param keyword --keyword to search for could be table alias, column name 
   * @param indexToSearch -- 0 to search for table name , 1 to search for column name
   * @returns 
   */
  ruleHasKeyword(rule :IRuleWithQueryFilters, keyword: string, indexToSearch: number)
  {
    return (rule.rightOperandType ==1 && rule.rightOperand.split('.')[indexToSearch] === keyword) || (rule.leftOperandType ==1 && rule.leftOperand.split('.')[indexToSearch] === keyword) 
  }

  getQueryColumnsAsDropdownOptions() {
    const tableOptions: IDropDownOptions[] = [];
    let columnCount = 0;
    this.queryDataUI?.queryTables.forEach(entityCard => {
      let aTableOption = this.createParentDropdownOption(`${columnCount++}`, entityCard.tableAlias, entityCard.tableAlias);
      this.queryDataUI?.queryTableWithColumnDataList.filter(column => column.schemaName === entityCard.schemaName && column.tableName === entityCard.tableName)?.map((column: IQueryTableWithColumnDataList, index) => {
       aTableOption.childern?.push(this.createChildDropdownOption(`${index}`, `${column.columnName}`, `${entityCard.tableAlias}.${column.columnName}`));
      });
      tableOptions.push(aTableOption);
    });
    return tableOptions;
  }

  convertColumnsToOption(columns: GetPossibleColumnsResponse[]) {
    var grouped = this.groupByPipe.transform(columns, 'tableAlias');
    let options: IDropDownOptions[] = [];
    let columnCount = 0;
    Object.entries(grouped).forEach(alias => {
      let tableAlias = alias[0];
      // Table Option
      let aTableOption = this.createParentDropdownOption(`${columnCount++}`, tableAlias, tableAlias);
      // Column Options 
      let childOptions = alias[1] as GetPossibleColumnsResponse[];
      if (childOptions.length > 0) {
        childOptions.map((item, index) => {
          aTableOption?.childern?.push(this.createChildDropdownOption(`${index}`, `${item.columnName}`, `${item.tableAlias}.${item.columnName}`));
        });
      }
      options.push(aTableOption);
    })
    return options;
  }

  createParentDropdownOption(index: string, label: string, value: string,) {
    return {
      disabled: "false",
      listItemId: index,
      listLabel: label,
      listValue: value,
      moreOption: true,
      childern: [],
    } as IDropDownOptions;

  }

  createChildDropdownOption(index: string, label: string, value: string,) {
    return {
      disabled: "false",
      listItemId: index,
      listLabel: label,
      listValue: value,
      moreOption: false,
    } as IDropDownOptions;

  }

  removeQueryFilters(filtersToremove: IRuleWithQueryFilters[]) {
    if (filtersToremove != undefined && filtersToremove.length > 0) {
      filtersToremove.forEach((item) => {
        let filterIndex = this.queryDataUI.ruleWithQueryFilters?.indexOf(item);
        if (filterIndex != undefined && filterIndex !== -1) {
          this.queryDataUI.ruleWithQueryFilters?.splice(filterIndex, 1);
        }
      });
      this.emitQueryRuleschanged();
    }
  }

  updateSortOrderByIndex(items:any[]) {
    items?.forEach(
      (item, index) => (item.sortOrder = index + 1));
  }

  getQueryTableWithColumns(schema: string, tableName: string) {
    var tableInfo = this.queryDataUI?.queryTables?.find(table => table.schemaName == schema && table.tableName == tableName);
    return { ...tableInfo, tableColumnData: this.getCopyOfTableColumns(schema, tableName) };
  }

  isTableExistsInQuery(schema: string, tableName: string) {
    return this.queryDataUI?.queryTables?.some(table => table.schemaName == schema && table.tableName == tableName) ?? false;
  }

  // To get new copy of columns that are alredy present in common object and sort them based on PK and Name
  getCopyOfTableColumns(schema: string, tableName: string)
  {
    return this.queryDataUI?.queryTableWithColumnDataList?.filter(column => column.schemaName == schema && column.tableName == tableName).map((col) => ({...col })).sort((a, b) => {
      // If a.isprkey is true and b.isprkey is false, a should come first
      if (a.isPk && !b.isPk) {
        return -1;
      }
      // If a.isprkey is false and b.isprkey is true, b should come first
      if (!a.isPk && b.isPk) {
        return 1;
      }
      // If both a.isprkey and b.isprkey are true, or both are false, keep their relative order
      return 0;
    });
  }
 

  // Observables
  queryTablesRemovedSubject : BehaviorSubject<number> = new BehaviorSubject<any>(null);
  queryTablesRemovedSubjectCast = this.queryTablesRemovedSubject.asObservable();
  queryUpdatedSubject: BehaviorSubject<IQueryDataForDesignerResponse> =
    new BehaviorSubject<any>(null);
  queryUpdatedCast = this.queryUpdatedSubject.asObservable();

  queryRulesUpdatedSubject: BehaviorSubject<IRuleWithQueryFilters[]> = new BehaviorSubject<any>(null);
  queryRulesUpdatedCast = this.queryRulesUpdatedSubject.asObservable();

  querySaveSuccess: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  querySaveSuccessCast = this.querySaveSuccess.asObservable();

  querySearchedFormSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  newQuerySearchTemplateSubject: BehaviorSubject<any> =
    new BehaviorSubject<any>(null);
  queryRunFormSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  queryRunFormSubjectCast = this.queryRunFormSubject.asObservable();
  queryManagerInitSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  queryManagerInitSubjectCast = this.queryManagerInitSubject.asObservable();
  queryManagerAddTableSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  queryManagerAddTableSubjectCast =
    this.queryManagerAddTableSubject.asObservable();
  querySearchedFormSubjectCast = this.querySearchedFormSubject.asObservable();
  newQuerySearchTemplateSubjectCast =
    this.newQuerySearchTemplateSubject.asObservable();
  createQueryFormSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  createQueryFormSubjectCast = this.createQueryFormSubject.asObservable();
  hitGetcreateNewQueryFormData: BehaviorSubject<any> = new BehaviorSubject<any>(
    false
  );
  hitGetcreateNewQueryFormDataCast =
    this.hitGetcreateNewQueryFormData.asObservable();
  getRadioTemplateSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  getRadioTemplateSubjectCast = this.getRadioTemplateSubject.asObservable();
  getTableDataSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  getTableDataSubjectCast = this.getTableDataSubject.asObservable();
  getMostUsedTableDataSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  getMostUsedTableDataSubjectCast =
    this.getMostUsedTableDataSubject.asObservable();

  clearCreateNewQueryFormDataSubject: BehaviorSubject<any> =
    new BehaviorSubject<any>(null);
  clearCreateNewQueryFormDataSubjectCast =
    this.clearCreateNewQueryFormDataSubject.asObservable();
  setSidebarOpenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  setSidebarOpenSubjectCast = this.setSidebarOpenSubject.asObservable();

  querySliderSubject: BehaviorSubject<any> = new BehaviorSubject<any>(false);
  querySliderSubjectCast = this.querySliderSubject.asObservable();

  addNewDataSet: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  addNewDataSetCast = this.addNewDataSet.asObservable();

  dataSetUpdated: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  dataSetUpdatedCast = this.dataSetUpdated.asObservable();

  dataSetToEdit: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  dataSetdataSetToEditCast = this.dataSetToEdit.asObservable();

  entityCardColumnToJoin: Subject<IQueryTableWithColumnDataList> = new Subject<any>();
  entityCardColumnToJoinCast = this.entityCardColumnToJoin.asObservable();
  signalRConnectionCheckOutOn: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  signalRConnectionCheckOutOnCast =
    this.signalRConnectionCheckOutOn.asObservable();
  childNotificationData: Subject<IChildNotification> = new Subject<IChildNotification>();
  childNotificationDataCast = this.childNotificationData.asObservable();
  summarizationUpdated: BehaviorSubject<[number, { dataSet: IQueryTableWithQuerySelects, queryCategory: number, close: boolean }, boolean]> = new BehaviorSubject<[number, { dataSet: IQueryTableWithQuerySelects, queryCategory: number, close: boolean }, boolean]>([0, { dataSet: { tableAlias: '', tableName: '', columnAlias: '', columnName: '', queryId: 0, queryTableId: 0, summarization: 0 }, queryCategory: 0, close: false }, false]);
}
